import { useMutation } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';

export interface ParentsBulkUploadQueuedResponse {
  success: boolean;
  jobId: string;
  totalRecords: number;
  message: string;
  correlationId: string;
  estimatedTime: string;
  deduplicated?: boolean;
}

/** Legacy synchronous bulk-import body (pre–async job queue). */
export interface ParentsBulkUploadImmediateResponse {
  success: number;
  failed: number;
  errors: string[];
  batchId: string | null;
}

export type ParentsBulkUploadResponse =
  | ParentsBulkUploadQueuedResponse
  | ParentsBulkUploadImmediateResponse;

export function isParentsBulkUploadQueuedResponse(
  response: ParentsBulkUploadResponse,
): response is ParentsBulkUploadQueuedResponse {
  return (
    typeof (response as ParentsBulkUploadQueuedResponse).jobId === 'string' &&
    (response as ParentsBulkUploadQueuedResponse).jobId.length > 0 &&
    typeof (response as ParentsBulkUploadQueuedResponse).success === 'boolean'
  );
}

export const bulkUploadParentsAPI = async (
  file: File,
  onUploadProgress?: (percent: number) => void,
) => {
  const formData = new FormData();

  formData.append('parents', file);

  const resp = await baseAPI.post<ParentsBulkUploadResponse>(
    '/parents/bulk-import',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (evt) => {
        if (!onUploadProgress || !evt.total) return;
        onUploadProgress(Math.round((evt.loaded / evt.total) * 100));
      },
    },
  );
  return resp.data;
};

export const useBulkUploadParentsMutation = () => {
  return useMutation({
    mutationFn: async (input: {
      file: File;
      onUploadProgress?: (percent: number) => void;
    }) => {
      const response = await bulkUploadParentsAPI(
        input.file,
        input.onUploadProgress,
      );
      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PARENTS,
      });
    },
  });
};
